import { type SxProps, type Theme } from "@mui/material";
import {
  type GridPaginationModel,
  type DataGridProps,
  type GridFilterModel,
  type GridSortModel,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";

import { TableQueryParams } from "api/common/adapters/pagination.types";
import { PaginatedResponse } from "api/common/types/response.types";

import DefaultDataGrid from "./DefaultDataGrid";

interface TableModels {
  sort: GridSortModel;
  pagination: GridPaginationModel;
  search: GridFilterModel;
}

const dataGridToStandard = ({
  sort,
  pagination,
  search,
}: TableModels): TableQueryParams => ({
  page: pagination.page,
  pageSize: pagination.pageSize,
  sort: sort[0]
    ? { field: sort[0].field, direction: sort[0].sort ?? "desc" }
    : { field: "", direction: "" },
  search: search.quickFilterValues?.join(",") ?? "",
});

interface ServerSidePaginatedProps<T> extends DataGridProps {
  data: PaginatedResponse<T> | undefined;
  title: string;

  initialPaginationModel?: GridPaginationModel;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  defaultSortField?: string;

  tableParams: TableQueryParams;
  onParamsChange: (_update: TableQueryParams) => void;
  disableZebraStripes?: boolean;

  sx?: SxProps<Theme>;
  showToolbar?: boolean;
}

const defaultSortModel: GridSortModel = [];
const defaultFilterModel: GridFilterModel = { items: [] };
const defaultPaginationModel: GridPaginationModel = {
  page: 0,
  pageSize: 25,
};

const defaultPageSizeOptions = [5, 10, 25, 50, 100];

const ServerSidePaginatedDataGrid = <T,>({
  data,
  loading = false,
  title,

  initialPaginationModel,
  initialSortModel,
  initialFilterModel,
  showToolbar,
  disableZebraStripes = false,

  tableParams,
  onParamsChange,
  ...props
}: ServerSidePaginatedProps<T>) => {
  const [tableModels, setTableModels] = useState<TableModels>({
    sort: initialSortModel ?? defaultSortModel,
    pagination: initialPaginationModel ?? defaultPaginationModel,
    search: initialFilterModel ?? defaultFilterModel,
  });

  const updateTableModels = useCallback(
    (updater: (_prev: TableModels) => TableModels) => {
      setTableModels((prev) => {
        const next = updater(prev);
        onParamsChange(dataGridToStandard(next));
        return next;
      });
    },
    [onParamsChange],
  );

  useEffect(() => {
    if (tableParams.page === 0) {
      setTableModels((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, page: 0 },
      }));
    }
  }, [tableParams.page, updateTableModels]);

  // Searching/Filtering
  const handleFilterModelChange = (filterUpdate: GridFilterModel) => {
    updateTableModels((prev) => ({
      ...prev,
      search: filterUpdate,
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const handleSortModelChange = (sortUpdate: GridSortModel) => {
    updateTableModels((prev) => ({
      ...prev,
      sort: sortUpdate,
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const handlePaginationModelChange = (
    paginationUpdate: GridPaginationModel,
  ) => {
    updateTableModels((prev) => {
      const prevPageSize = prev.pagination.pageSize;
      const newPageSize = paginationUpdate.pageSize;

      if (newPageSize !== prevPageSize) paginationUpdate.page = 0;

      return {
        ...prev,
        pagination: { ...paginationUpdate },
      };
    });
  };

  return (
    <DefaultDataGrid
      title={title}
      loading={loading}
      rows={data?.results}
      rowCount={data?.count ?? -1}
      pageSizeOptions={defaultPageSizeOptions}
      paginationMode="server"
      filterMode="server"
      sortingMode="server"
      filterDebounceMs={300}
      paginationModel={tableModels.pagination}
      sortModel={tableModels.sort}
      filterModel={tableModels.search}
      onPaginationModelChange={handlePaginationModelChange}
      onFilterModelChange={handleFilterModelChange}
      onSortModelChange={handleSortModelChange}
      showToolbar={showToolbar}
      disableZebraStripes={disableZebraStripes}
      autoHeight // i dont really know what to do about this one.
      // there is a really weird table dancing bug that happens inconsistently without it
      // but it is marked as deprecated. Im leaving it in for now but should reevaluate at some point
      {...props}
    />
  );
};
export default ServerSidePaginatedDataGrid;
